import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import EmailReportDialog from "@/components/EmailReportDialog";
import ClientHistoryDialog from "@/components/ClientHistoryDialog";
import DocumentsSection from "@/components/DocumentsSection";
import QuoteAcceptedDialog from "@/components/QuoteAcceptedDialog";
import { enqueue, isOnline } from "@/lib/offlineQueue";
import ChantierHeader from "./chantier/ChantierHeader";
import ChantierTabs from "./chantier/ChantierTabs";
import OverviewTab from "./chantier/tabs/OverviewTab";
import MaterialTab from "./chantier/tabs/MaterialTab";
import PhotosTab from "./chantier/tabs/PhotosTab";
import SignaturesTab from "./chantier/tabs/SignaturesTab";
import ChatTab from "./chantier/tabs/ChatTab";
import PanelAnalyzer from "@/components/PanelAnalyzer";

export default function ChantierDetail() {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const [chantier, setChantier] = useState(null);
  const [client, setClient] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [interventions, setInterventions] = useState([]);
  const [signaturesList, setSignaturesList] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("overview");
  const [newTask, setNewTask] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [quoteAcceptedOpen, setQuoteAcceptedOpen] = useState(false);

  const load = useCallback(async () => {
    const c = await api.get(`/chantiers/${id}`);
    setChantier(c.data);
    if (c.data.client_id) {
      const cl = await api.get(`/clients/${c.data.client_id}`);
      setClient(cl.data);
    }
    const [t, s, p, o, iv] = await Promise.all([
      api.get(`/chantiers/${id}/tasks`),
      api.get("/signatures", { params: { chantier_id: id } }),
      api.get("/photos", { params: { chantier_id: id } }),
      api.get("/material-orders", { params: { chantier_id: id } }),
      api.get("/interventions").catch(() => ({ data: [] })),
    ]);
    setTasks(t.data);
    setSignaturesList(s.data);
    setPhotos(p.data);
    setOrders(o.data);
    setInterventions((iv.data || []).filter((i) => i.chantier_id === id));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!chantier) return <div className="p-8 text-white/50">Chargement…</div>;

  const patch = async (updates) => {
    const r = await api.patch(`/chantiers/${id}`, updates);
    setChantier(r.data);
  };

  const addTask = async () => {
    if (!newTask.trim()) return;
    const label = newTask.trim();
    setNewTask("");
    if (!isOnline()) {
      const tempId = `tmp_${Date.now()}`;
      const optimistic = { id: tempId, chantier_id: id, label, done: false, created_at: new Date().toISOString(), _offline: true };
      setTasks((t) => [...t, optimistic]);
      enqueue({ method: "post", url: `/chantiers/${id}/tasks`, data: { label } });
      toast.info("Tâche enregistrée hors ligne — sera synchronisée");
      return;
    }
    try {
      const r = await api.post(`/chantiers/${id}/tasks`, { label });
      setTasks((t) => [...t, r.data]);
    } catch {
      const tempId = `tmp_${Date.now()}`;
      setTasks((t) => [...t, { id: tempId, chantier_id: id, label, done: false, created_at: new Date().toISOString(), _offline: true }]);
      enqueue({ method: "post", url: `/chantiers/${id}/tasks`, data: { label } });
      toast.info("Tâche enregistrée hors ligne");
    }
  };

  const toggleTask = async (task) => {
    const newDone = !task.done;
    setTasks((ts) => ts.map((t) => (t.id === task.id ? { ...t, done: newDone } : t)));
    if (task._offline || task.id.startsWith("tmp_") || !isOnline()) {
      enqueue({ method: "patch", url: `/tasks/${task.id}`, data: { done: newDone } });
      return;
    }
    try {
      await api.patch(`/tasks/${task.id}`, { done: newDone });
    } catch {
      enqueue({ method: "patch", url: `/tasks/${task.id}`, data: { done: newDone } });
    }
  };

  const deleteTask = async (task) => {
    setTasks((ts) => ts.filter((t) => t.id !== task.id));
    if (task._offline || task.id.startsWith("tmp_") || !isOnline()) {
      enqueue({ method: "delete", url: `/tasks/${task.id}` });
      return;
    }
    try {
      await api.delete(`/tasks/${task.id}`);
    } catch {
      enqueue({ method: "delete", url: `/tasks/${task.id}` });
    }
  };

  const totalTasks = tasks.length;
  const doneTasks = tasks.filter((t) => t.done).length;
  const computedProgress = totalTasks ? Math.round((doneTasks / totalTasks) * 100) : chantier.progress;

  return (
    <div className="p-4 space-y-4 pb-8">
      <ChantierHeader
        chantier={chantier}
        client={client}
        isAdmin={isAdmin}
        computedProgress={computedProgress}
        onStatusChange={(v) => patch({ status: v })}
        onOpenHistory={() => setHistoryOpen(true)}
        onChanged={load}
      />

      <ChantierTabs tab={tab} onChange={setTab} />

      {tab === "overview" && (
        <OverviewTab
          chantier={chantier}
          client={client}
          interventions={interventions}
          tasks={tasks}
          doneTasks={doneTasks}
          totalTasks={totalTasks}
          newTask={newTask}
          setNewTask={setNewTask}
          addTask={addTask}
          toggleTask={toggleTask}
          deleteTask={deleteTask}
          onChanged={load}
          onOpenEmail={() => setEmailOpen(true)}
          onOpenQuoteAccepted={() => setQuoteAcceptedOpen(true)}
        />
      )}

      {tab === "material" && (
        <MaterialTab chantier={chantier} orders={orders} onChanged={load} isAdmin={isAdmin} />
      )}
      {tab === "panel" && (
        <PanelAnalyzer chantierId={id} />
      )}

      {tab === "documents" && (
        <DocumentsSection chantierId={id} isAdmin={isAdmin} />
      )}

      {tab === "chat" && <ChatTab chantierId={id} />}

      {tab === "photos" && (
        <PhotosTab chantierId={id} photos={photos} onChanged={load} />
      )}

      {tab === "signatures" && (
        <SignaturesTab chantierId={id} client={client} signaturesList={signaturesList} onChanged={load} />
      )}

      <EmailReportDialog
        open={emailOpen}
        onClose={() => setEmailOpen(false)}
        chantier={chantier}
        client={client}
      />
      <ClientHistoryDialog
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        clientId={client?.id}
      />
      <QuoteAcceptedDialog
        open={quoteAcceptedOpen}
        onClose={() => setQuoteAcceptedOpen(false)}
        kind="chantier"
        entity={chantier}
        estimatedAmount={chantier?.estimated_amount}
        onSaved={() => { setQuoteAcceptedOpen(false); load(); }}
      />
    </div>
  );
}
